import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pause, Play, RotateCcw, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

interface RolloutDetailsProps {
  rolloutId: string;
  onBack: () => void;
}

export const RolloutDetails = ({ rolloutId, onBack }: RolloutDetailsProps) => {
  const { invokeAdmin, isLoading: actionLoading } = useAdminEdgeFunction();
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [resumeNotes, setResumeNotes] = useState("");
  const [rollbackReason, setRollbackReason] = useState("");

  const { data: rollout, isLoading, refetch } = useQuery({
    queryKey: ["rollout-details", rolloutId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_rollouts" as any)
        .select(`
          *,
          prompt_versions:version_id (
            version_number,
            prompt_text,
            prompt_templates:template_id (name, function_name)
          )
        `)
        .eq("id", rolloutId)
        .single();

      if (error) throw error;
      return data as any;
    },
  });

  const handlePause = async () => {
    if (!pauseReason.trim()) {
      toast.error("Please provide a reason for pausing");
      return;
    }

    const { error } = await invokeAdmin("pause-rollout", {
      body: { rolloutId, reason: pauseReason },
      showErrorToast: true,
    });

    if (!error) {
      toast.success("Rollout paused successfully");
      setShowPauseDialog(false);
      setPauseReason("");
      refetch();
    }
  };

  const handleResume = async () => {
    const { error } = await invokeAdmin("resume-rollout", {
      body: { rolloutId, notes: resumeNotes },
      showErrorToast: true,
    });

    if (!error) {
      toast.success("Rollout resumed successfully");
      setShowResumeDialog(false);
      setResumeNotes("");
      refetch();
    }
  };

  const handleRollback = async () => {
    if (!rollbackReason.trim()) {
      toast.error("Please provide a reason for rollback");
      return;
    }

    const { error } = await invokeAdmin("rollback-prompt-version", {
      body: { versionId: rollout?.version_id, reason: rollbackReason },
      showErrorToast: true,
    });

    if (!error) {
      toast.success("Version rolled back successfully");
      setShowRollbackDialog(false);
      setRollbackReason("");
      refetch();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!rollout) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium text-foreground">Rollout not found</p>
        <Button onClick={onBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">Rollout Details</h2>
          <p className="text-muted-foreground">
            {rollout.prompt_versions?.prompt_templates?.name} v
            {rollout.prompt_versions?.version_number}
          </p>
        </div>
        <div className="flex gap-2">
          {rollout.status === "active" && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowPauseDialog(true)}
                disabled={actionLoading}
              >
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowRollbackDialog(true)}
                disabled={actionLoading}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Rollback
              </Button>
            </>
          )}
          {rollout.status === "paused" && (
            <Button
              onClick={() => setShowResumeDialog(true)}
              disabled={actionLoading}
            >
              <Play className="h-4 w-4 mr-2" />
              Resume
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Rollout Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <Badge>{rollout.status}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">
                {rollout.rollout_type === "gradual" ? "Gradual Rollout" : "A/B Test"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Traffic:</span>
              <span className="font-medium">{rollout.traffic_percentage}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ring:</span>
              <Badge variant="outline">{rollout.ring}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created:</span>
              <span className="font-medium">
                {new Date(rollout.created_at).toLocaleString()}
              </span>
            </div>
            {rollout.start_date && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started:</span>
                <span className="font-medium">
                  {new Date(rollout.start_date).toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Version Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Template:</span>
              <span className="font-medium">
                {rollout.prompt_versions?.prompt_templates?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Function:</span>
              <span className="font-medium">
                {rollout.prompt_versions?.prompt_templates?.function_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Version:</span>
              <span className="font-medium">v{rollout.prompt_versions?.version_number}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {rollout.paused_at && (
        <Card>
          <CardHeader>
            <CardTitle>Pause Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Paused at:</span>
              <span className="font-medium">
                {new Date(rollout.paused_at).toLocaleString()}
              </span>
            </div>
            {rollout.pause_reason && (
              <div>
                <span className="text-muted-foreground">Reason:</span>
                <p className="mt-1">{rollout.pause_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {rollout.resumed_at && (
        <Card>
          <CardHeader>
            <CardTitle>Resume Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Resumed at:</span>
              <span className="font-medium">
                {new Date(rollout.resumed_at).toLocaleString()}
              </span>
            </div>
            {rollout.resume_notes && (
              <div>
                <span className="text-muted-foreground">Notes:</span>
                <p className="mt-1">{rollout.resume_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pause Dialog */}
      <AlertDialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pause Rollout</AlertDialogTitle>
            <AlertDialogDescription>
              This will temporarily stop the rollout. You can resume it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-4">
            <Label htmlFor="pauseReason">Reason for pausing (required)</Label>
            <Textarea
              id="pauseReason"
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              placeholder="Enter the reason for pausing this rollout..."
              className="min-h-[100px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePause} disabled={actionLoading}>
              {actionLoading ? "Pausing..." : "Pause Rollout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Dialog */}
      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume Rollout</AlertDialogTitle>
            <AlertDialogDescription>
              This will resume the rollout from where it was paused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-4">
            <Label htmlFor="resumeNotes">Notes (optional)</Label>
            <Textarea
              id="resumeNotes"
              value={resumeNotes}
              onChange={(e) => setResumeNotes(e.target.value)}
              placeholder="Add any notes about resuming this rollout..."
              className="min-h-[100px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResume} disabled={actionLoading}>
              {actionLoading ? "Resuming..." : "Resume Rollout"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback Dialog */}
      <AlertDialog open={showRollbackDialog} onOpenChange={setShowRollbackDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rollback Version</AlertDialogTitle>
            <AlertDialogDescription className="text-destructive">
              This is a destructive action. It will immediately deactivate this version and
              revert to the previous one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-4">
            <Label htmlFor="rollbackReason">Reason for rollback (required)</Label>
            <Textarea
              id="rollbackReason"
              value={rollbackReason}
              onChange={(e) => setRollbackReason(e.target.value)}
              placeholder="Enter the reason for rolling back this version..."
              className="min-h-[100px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRollback}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? "Rolling back..." : "Rollback Version"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
